import { useCallback } from 'react';
import type { ListItem } from '@/pages/Shipments/ControlTower/types';
import { themeT } from '@/utils/themeText';
import theme from '@/assets/theme';

type UseInventoryFilterConfiguratorParams = {
  setIsFilterOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSecondaryFiltersDDL: React.Dispatch<React.SetStateAction<ListItem[]>>;
  setSecondaryFiltersSource: React.Dispatch<React.SetStateAction<ListItem[]>>;
  setIsDatePicker: React.Dispatch<React.SetStateAction<boolean>>;
  setFilterTitle: React.Dispatch<React.SetStateAction<string>>;
  lists?: {
    savedFiltersSets?: ListItem[];
    locations?: ListItem[];
    teams?: ListItem[];
    recordTypes?: ListItem[];
    stockVsRequiredStates?: ListItem[];
    stockPlanningStates?: ListItem[];
    notificationSetupOptions?: ListItem[];
    structures?: ListItem[];
  };
  loaders?: {
    teams?: () => Promise<ListItem[]>;
    locations?: () => Promise<ListItem[]>;
  };
};

export default function useInventoryFilterConfigurator({
  setIsFilterOpen,
  setSecondaryFiltersDDL,
  setSecondaryFiltersSource,
  setIsDatePicker,
  setFilterTitle,
  lists,
  loaders,
}: UseInventoryFilterConfiguratorParams): (filterKey: string) => void {
  const setBoth = useCallback(
    (list: ListItem[]) => {
      const safe = Array.isArray(list) ? list : [];
      setSecondaryFiltersSource(safe);
      setSecondaryFiltersDDL(safe);
    },
    [setSecondaryFiltersDDL, setSecondaryFiltersSource]
  );

  return useCallback(
    (fb: string) => {
      setIsFilterOpen(false);
      setBoth([]);
      setIsDatePicker(false);

      switch (fb) {
        case 'structure':
          setFilterTitle('Structure');
          setBoth(lists?.structures || []);
          break;

        case 'locations':
          setFilterTitle('Location');
          if (lists?.locations?.length) {
            setBoth(lists.locations);
          } else {
            loaders?.locations?.().then(setBoth);
          }
          break;

        case 'team': {
          const title = `${themeT('team', theme.themeType)}`;
          setFilterTitle(title);
          if (lists?.teams?.length) {
            setBoth(lists.teams);
          } else {
            loaders?.teams?.().then(setBoth);
          }
          break;
        }

        case 'record_type':
          setFilterTitle('Record type');
          setBoth(lists?.recordTypes || []);
          break;

        case 'stock_vs_required_state':
          setFilterTitle('Stock vs required state');
          setBoth(lists?.stockVsRequiredStates || []);
          break;

        case 'stock_planning_state':
          setFilterTitle('Stock planning state');
          setBoth(lists?.stockPlanningStates || []);
          break;

        case 'notification_setup':
          setFilterTitle('Notification setup');
          setBoth(lists?.notificationSetupOptions || []);
          break;

        case 'last_updated':
          setFilterTitle('Last updated');
          setIsDatePicker(true);
          setBoth([]);
          break;
        // case 'saved_filters':
        //   setFilterTitle('Saved filters sets');
        //   setBoth(lists?.savedFiltersSets || []);
        //   break;
        default:
          setFilterTitle(
            fb.replace(/_/g, ' ').replace(/^\w/, (char) => char.toUpperCase())
          );
          setBoth([]);
      }
    },
    [lists, loaders, setBoth, setFilterTitle, setIsDatePicker, setIsFilterOpen]
  );
}
